import { Controller, FieldError, useForm } from "react-hook-form";
import { ModalProps, createUseModal, useModal } from "@imminently/react/context/modals";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import { Button, IconButton, Stack, TextField, Select, MenuItem } from "@mui/material";
import { Icon } from "@/components/Icon";
import { encounterService } from "@/services";
import { Allergy, PatientEncounter } from "@/resources/types";
import { css } from "@/theme";
import { omit } from "lodash";
import { useTypes } from "@/providers/TypesProvider";


const styles = {
  allergyBox: css`
    color: #161B32;
  `,
  selectBox: css`
    .MuiSelect-select {
      padding: 0.5rem 1rem;
    }

  `
};

const initialState = {
  type: "",
  severity: ""
};

const errorProps = (error: FieldError | undefined) => (
  error ? { error: true, helperText: error.message } : {}
);

const { useMutateResource } = encounterService;

const AddAllergyModal = (
  { props }: ModalProps<{ encounter: any }>
) => {
  const { mutateAsync: updateEncounter } = encounterService.useUpdate();
  const { action, query } = useMutateResource(undefined, {});
  const { allergySeverityOptions } = useTypes();

  const { popModal: close } = useModal();

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    setError,
  } = useForm({ mode: "all", defaultValues: initialState });

  const handleCreate = async (data: any) => {

    const newAllergy: Partial<Allergy> = {
      description: data.type,
      ...(data.severity ? { severity: data.severity } : {}),
    };
    const allAllergies: Allergy[] = [...(props.encounter.patient.allergies || []), newAllergy].map(allergy => omit(allergy, ["source"]) as Allergy);
    //console.log("[Add allergy]", data);

    try {
      updateEncounter({
        id: props.encounter.id,
        payload: {
          // @ts-ignore We are just updating allergies
          patient: {
            allergies: allAllergies
          }
        }
      });
      close();
    } catch (error: any) {
      alert(error.message);
    }
  };

  return (
    <Dialog open onClose={close} maxWidth="sm">
      <DialogTitle mb={3}>
        <Text variant="h3">Add allergy</Text>
        <IconButton onClick={close}><Icon icon="close" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ width: "34rem", pt: 1, pb: 3 }}>
        <Stack>
          <Stack direction="row">
            <Text pb={0.8} variant="body2" bold>Allergen</Text>
          </Stack>
          <Stack direction="row" pb={2}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Allergen is required" }}
              render={({ field }) => (
                <TextField
                  css={styles.allergyBox}
                  fullWidth
                  required
                  placeholder="Allergen"
                  {...errorProps(errors.type)}
                  {...field}
                />
              )}
            />
          </Stack>
          <Stack direction="row">
            <Text pb={0.8} variant="body2" bold>Reaction</Text>
          </Stack>
          <Stack direction="row">
            <Controller
              name="severity"
              control={control}
              render={({ field }) => (
                <Select
                  fullWidth
                  css={styles.selectBox}
                  slotProps={{
                    input: {
                      placeholder: "Allergy reaction"
                    }
                  }}
                  {...errorProps(errors.severity)}
                  {...field}
                >
                  {allergySeverityOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button disabled={!isValid || query.isLoading} onClick={handleSubmit(handleCreate)}>
          Add allergy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useAddAllergyModal = createUseModal(AddAllergyModal);
